import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { CardImage } from '../CardImage';
import { CardBugReportModal } from './CardBugReportModal';
import { useAuth } from '../AuthProvider';
import { useNotification } from './NotificationProvider';
import {
  getBinders,
  addCardToList,
  addCardToBinder,
  removeCardFromList,
  removeCardFromBinder
} from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { convertUsdToBrl } from '../../services/cardPriceService';

export interface FolderCardDetailModalProps {
  card: any | null;
  folder: {
    id: string;
    name: string;
    icon?: string;
    color?: string;
    isSystem?: boolean;
    game_id?: any;
    game_name?: string;
    game?: string;
  } | null;
  canEdit: boolean;
  onClose: () => void;
  cardsList?: any[];
  onSelectCard?: (card: any) => void;
  onUpdateQuantity?: (card: any, delta: number) => void;
  onSetDirectQuantity?: (card: any, newQuantity: number) => void;
  onDeleteCard?: (card: any) => void;
  onCardMoved?: (card: any) => void;
  gameName?: string;
  ownerProfile?: any | null;
  ownerId?: string | null;
}

const DetailField: React.FC<{
  label: string;
  value: any;
  highlight?: string;
  isCircle?: boolean;
  icon?: string;
}> = ({ label, value, highlight = 'text-white', isCircle, icon }) => {
  if (value === null || value === undefined) return null;
  const displayVal = String(value).trim();
  if (!displayVal || displayVal === '[object Object]') return null;

  const showCircle = isCircle && displayVal.length <= 3;

  return (
    <div className="space-y-1">
      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
      <div className="bg-slate-950 border border-slate-800/80 rounded-lg px-2.5 py-1.5 flex items-center justify-between">
        <span className={`text-xs font-semibold ${highlight} truncate`} title={displayVal}>
          {displayVal}
        </span>
        {showCircle ? (
          <span className="w-4 h-4 bg-slate-800 text-slate-200 text-[9px] font-bold rounded-full flex items-center justify-center shrink-0 ml-1.5">
            {displayVal}
          </span>
        ) : icon ? (
          <i className={`${icon} text-slate-500 text-[10px] ml-1.5`}></i>
        ) : null}
      </div>
    </div>
  );
};

export const FolderCardDetailModal: React.FC<FolderCardDetailModalProps> = ({
  card,
  folder,
  canEdit,
  onClose,
  cardsList = [],
  onSelectCard,
  onUpdateQuantity,
  onSetDirectQuantity,
  onDeleteCard,
  onCardMoved,
  gameName,
  ownerProfile,
  ownerId,
}) => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const isOfferlist = folder?.id === 'offerlist';

  const [dbCard, setDbCard] = useState<any | null>(null);
  const [fullscreenImageCard, setFullscreenImageCard] = useState<any | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);

  // Transfer / Copy state
  const [isTransferModalOpen, setIsTransferModalOpen] = useState<boolean>(false);
  const [transferAction, setTransferAction] = useState<'move' | 'copy'>('copy');
  const [targetDestination, setTargetDestination] = useState<string>('colecao');
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState<boolean>(false);
  const [userBinders, setUserBinders] = useState<any[]>([]);

  // Delete confirmation state
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
  const [isDeletingCard, setIsDeletingCard] = useState<boolean>(false);

  // Wishlist quick action state
  const [isAddingToWishlist, setIsAddingToWishlist] = useState<boolean>(false);
  const [isInWishlist, setIsInWishlist] = useState<boolean>(false);

  // Busca as informações da carta diretamente da tabela public.cards
  useEffect(() => {
    let isMounted = true;
    const fetchCardFromDb = async () => {
      if (!card) return;

      const cardRecordId = card.card_id || card.card_db_id || (typeof card.id === 'number' || (typeof card.id === 'string' && /^\d+$/.test(card.id)) ? Number(card.id) : null);

      try {
        let query = supabase
          .from('cards')
          .select(`
            id,
            external_id,
            game_id,
            name,
            image_url,
            price,
            variant_number,
            price_updated_at,
            set_code,
            rarity,
            is_foil,
            variant_external_id,
            variant_code,
            tcgplayer_id,
            games (name)
          `);

        if (cardRecordId) {
          query = query.eq('id', cardRecordId);
        } else {
          if (card.external_id && card.game_id) {
            query = query.eq('external_id', card.external_id).eq('game_id', card.game_id);
          } else if (card.external_id) {
            query = query.eq('external_id', card.external_id);
          } else {
            return;
          }
          const vNum = Number(card.variant_number ?? card.variantNumber);
          if (!isNaN(vNum) && vNum > 0) {
            query = query.eq('variant_number', vNum);
          }
        }

        const { data, error } = await query.maybeSingle();

        if (!error && data && isMounted) {
          setDbCard(data);
        }
      } catch (err) {
        console.error('Erro ao buscar dados da carta na tabela cards:', err);
      }
    };

    setDbCard(null);
    fetchCardFromDb();

    return () => {
      isMounted = false;
    };
  }, [card?.id, card?.card_id, card?.card_db_id, card?.external_id, card?.game_id, card?.variant_number, card?.variantNumber]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenImageCard) {
          setFullscreenImageCard(null);
        } else if (isTransferModalOpen) {
          setIsTransferModalOpen(false);
        } else if (isReportModalOpen) {
          setIsReportModalOpen(false);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        if (cardsList.length > 0 && onSelectCard && currentCardIndex > 0) {
          const prevItem = cardsList[currentCardIndex - 1];
          onSelectCard(prevItem?.card || prevItem);
        }
      } else if (e.key === 'ArrowRight') {
        if (cardsList.length > 0 && onSelectCard && currentCardIndex < cardsList.length - 1) {
          const nextItem = cardsList[currentCardIndex + 1];
          onSelectCard(nextItem?.card || nextItem);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Load user binders for transfer/copy destination dropdown
  useEffect(() => {
    if (!user) return;
    const unsub = getBinders(user.id, (binders) => {
      setUserBinders(binders || []);
    });
    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [user]);

  if (!card) return null;

  // Resolução estrita: todas as informações vêm apenas dos campos da tabela public.cards
  const resolvedCard = {
    id: dbCard?.id ?? (card.card_id || card.card_db_id || card.id),
    external_id: dbCard?.external_id ?? card.external_id ?? card.code ?? '',
    game_id: dbCard?.game_id ?? card.game_id ?? folder?.game_id,
    name: dbCard?.name ?? card.name ?? 'Carta Sem Nome',
    image_url: dbCard?.image_url ?? card.image_url ?? card.imageUrl ?? null,
    price: dbCard?.price !== undefined && dbCard?.price !== null
      ? convertUsdToBrl(Number(dbCard.price))
      : (typeof card.price === 'number' ? card.price : 0),
    variant_number: dbCard?.variant_number ?? card.variant_number ?? 1,
    price_updated_at: dbCard?.price_updated_at ?? card.price_updated_at ?? null,
    
    rarity: dbCard ? (dbCard.rarity ?? null) : (card.rarity ?? card.raridade ?? null),
    is_foil: dbCard ? Boolean(dbCard.is_foil) : Boolean(card.is_foil),
    
    set_code: dbCard ? (dbCard.set_code ?? null) : (card.set_code ?? null),
    variant_code: dbCard ? (dbCard.variant_code ?? null) : (card.variant_code ?? null),
    variant_external_id: dbCard ? (dbCard.variant_external_id ?? null) : (card.variant_external_id ?? null),
    tcgplayer_id: dbCard ? (dbCard.tcgplayer_id ?? null) : (card.tcgplayer_id ?? null),
    game_name: (dbCard?.games as any)?.name || card.game || gameName || folder?.game_name || ''
  };

  const currentQuantity = typeof card.quantity === 'number' ? card.quantity : 1;
  const unitPrice = typeof resolvedCard.price === 'number' && resolvedCard.price > 0 ? resolvedCard.price : 0;
  const totalPrice = unitPrice * currentQuantity;

  const formatPriceDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'Não registrada';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Informações da carta estruturadas exclusivamente a partir da tabela public.cards
  const fields: { label: string; value: any; highlight?: string; isCircle?: boolean; icon?: string }[] = [];

  // 1. ID no Banco (id - bigint)
  if (resolvedCard.id !== undefined && resolvedCard.id !== null) {
    fields.push({
      label: 'ID no Banco (ID)',
      value: `#${resolvedCard.id}`,
      highlight: 'text-purple-300 font-mono font-bold',
      icon: 'fas fa-database'
    });
  }
  // 2. ID Externo (external_id - text)
  fields.push({
    label: 'ID Externo',
    value: resolvedCard.external_id || '-',
    highlight: 'text-purple-300 font-mono font-bold',
    icon: 'fas fa-fingerprint'
  });
  // 3. Coleção (set_code - text)
  fields.push({
    label: 'Coleção',
    value: resolvedCard.set_code || '-',
    highlight: 'text-blue-300 font-semibold font-mono',
    icon: 'fas fa-layer-group'
  });
  // 8. Código da Variante (variant_code - text)
  if (resolvedCard.variant_code) {
    fields.push({
      label: 'Código da Variante',
      value: resolvedCard.variant_code,
      highlight: 'text-cyan-300 font-mono font-bold',
      icon: 'fas fa-barcode'
    });
  }
  // 7. Número da Variante (variant_number - integer)
  fields.push({
    label: 'Variante',
    value: `#${resolvedCard.variant_number ?? 1}`,
    highlight: 'text-cyan-300 font-mono font-bold',
    icon: 'fas fa-hashtag'
  });
  // 4. Raridade (rarity - text)
  fields.push({
    label: 'Raridade',
    value: resolvedCard.rarity || '-',
    highlight: 'text-orange-400 font-bold',
    icon: 'fas fa-gem'
  });
  // 10. Acabamento Foil (is_foil - boolean)
  fields.push({
    label: 'Acabamento',
    value: resolvedCard.is_foil ? 'Foil' : 'Vanilla',
    highlight: resolvedCard.is_foil ? 'text-yellow-300 font-bold' : 'text-slate-300 font-medium',
    icon: resolvedCard.is_foil ? 'fas fa-sparkles' : 'fas fa-square'
  });

  // 11. TCGPlayer ID (tcgplayer_id - text)
  if (resolvedCard.tcgplayer_id) {
    fields.push({
      label: 'TCGPlayer ID',
      value: resolvedCard.tcgplayer_id,
      highlight: 'text-blue-400 font-mono font-semibold',
      icon: 'fas fa-store'
    });
  }
    // 5. Preço Estimado (price - numeric(12, 2))
  const estimatedPriceStr = unitPrice > 0
    ? `R$ ${unitPrice.toFixed(2)}${currentQuantity > 1 ? ` (Total: R$ ${totalPrice.toFixed(2)})` : ''}`
    : 'R$ 0,00';
  fields.push({
    label: 'Preço Estimado',
    value: estimatedPriceStr,
    highlight: 'text-emerald-400 font-bold font-mono',
    icon: 'fas fa-tag'
  });

  // 6. Atualização do Preço (price_updated_at - timestamp with time zone)
  fields.push({
    label: 'Atualização do Preço',
    value: formatPriceDate(resolvedCard.price_updated_at),
    highlight: 'text-slate-300 font-mono text-[11px]',
    icon: 'fas fa-clock'
  });

  const tcgPlayerUrl = resolvedCard.tcgplayer_id
    ? `https://www.tcgplayer.com/product/${resolvedCard.tcgplayer_id}`
    : `https://www.tcgplayer.com/search/all/product?q=${encodeURIComponent(resolvedCard.name)}`;

  const getTargetCard = (item: any) => (item?.card ? item.card : item);

  const currentCardIndex = cardsList.length > 0
    ? cardsList.findIndex((c: any) => {
        const target = getTargetCard(c);
        if (target.dbId && card.dbId && target.dbId === card.dbId) return true;
        if (target.user_card_id && card.user_card_id && target.user_card_id === card.user_card_id) return true;
        if (target.id && card.id && String(target.id) === String(card.id)) return true;
        if (target.external_id && card.external_id && String(target.external_id) === String(card.external_id)) return true;
        if (target.card_id && card.card_id && String(target.card_id) === String(card.card_id)) return true;
        return false;
      })
    : -1;

  const handleContactOwner = () => {
    if (!user) {
      showNotification('Você precisa estar conectado para solicitar contato.', 'info');
      return;
    }
    onClose();
    navigate('/cardlink', {
      state: {
        targetUser: ownerProfile || (ownerId ? { id: ownerId } : null),
        initialCard: resolvedCard.name
      }
    });
  };

  const handleQuickAddToWishlist = async () => {
    if (!user) {
      showNotification('Você precisa estar logado para salvar na Wishlist.', 'info');
      return;
    }
    if (!card) return;
    setIsAddingToWishlist(true);
    try {
      await addCardToList(user.id, 'wishlist', card, 1);
      setIsInWishlist(true);
      showNotification(`"${resolvedCard.name}" adicionada à sua Wishlist!`, 'success');
    } catch (err: any) {
      console.error('Error adding to wishlist:', err);
      showNotification(err?.message || 'Erro ao adicionar à Wishlist', 'error');
    } finally {
      setIsAddingToWishlist(false);
    }
  };

  // Transfer / Copy execution
  const handleExecuteTransfer = async () => {
    if (!user) {
      showNotification('Você precisa estar logado para realizar esta ação', 'info');
      return;
    }

    setIsSubmittingTransfer(true);
    try {
      const isTargetSystem = targetDestination === 'colecao' || targetDestination === 'wishlist' || targetDestination === 'offerlist';
      const targetSystemList = targetDestination === 'colecao' ? 'cards' : targetDestination as 'wishlist' | 'offerlist';

      // 1. Add to target destination
      if (isTargetSystem) {
        await addCardToList(user.id, targetSystemList, card, 1);
      } else {
        await addCardToBinder(user.id, targetDestination, card);
      }

      // 2. If move, remove from current folder
      if (transferAction === 'move' && canEdit && folder) {
        if (folder.isSystem) {
          const fromSystemList = folder.id === 'colecao' ? 'cards' : folder.id as any;
          await removeCardFromList(user.id, fromSystemList, card.id);
        } else {
          await removeCardFromBinder(folder.id, card.dbId);
        }
        if (onCardMoved) {
          onCardMoved(card);
        }
      }

      const destName = isTargetSystem
        ? (targetDestination === 'colecao' ? 'Minha Coleção' : (targetDestination === 'wishlist' ? 'Wishlist' : 'Offerlist'))
        : (userBinders.find(b => String(b.id) === String(targetDestination))?.name || 'Pasta de Destino');

      showNotification(
        transferAction === 'move'
          ? `Carta movida para "${destName}" com sucesso!`
          : `Cópia da carta adicionada em "${destName}"!`,
        'success'
      );

      setIsTransferModalOpen(false);
      if (transferAction === 'move') {
        onClose();
      }
    } catch (err: any) {
      console.error('Error in transfer/copy:', err);
      showNotification(err?.message || 'Erro ao transferir carta', 'error');
    } finally {
      setIsSubmittingTransfer(false);
    }
  };

  // Direct deletion with dedicated confirmation
  const handleConfirmDelete = async () => {
    if (!user || !folder || !card) return;
    setIsDeletingCard(true);
    try {
      if (folder.isSystem) {
        const fromSystemList = folder.id === 'colecao' ? 'cards' : folder.id as any;
        await removeCardFromList(user.id, fromSystemList, card.id);
      } else {
        await removeCardFromBinder(folder.id, card.dbId);
      }
      showNotification(`"${resolvedCard.name}" foi removida de "${folder.name}"`, 'info');
      if (onCardMoved) {
        onCardMoved(card);
      } else if (onDeleteCard) {
        onDeleteCard(card);
      }
      setIsDeleteConfirmOpen(false);
      onClose();
    } catch (err: any) {
      console.error('Error removing card from folder:', err);
      showNotification(err?.message || 'Erro ao remover carta', 'error');
    } finally {
      setIsDeletingCard(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/80"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-4xl w-full bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden flex flex-col max-h-[92vh]"
          >
            {/* Context-Aware Header */}
            <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 border-b border-slate-800 bg-slate-950">
              <div className="flex items-center space-x-2.5 min-w-0">
                {/* Badge do Dono da Pasta */}
                {!canEdit && (ownerProfile || ownerId) && (
                  <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-purple-500/10 border border-purple-500/30 text-xs font-semibold text-purple-300 shrink-0">
                    {ownerProfile?.avatar ? (
                      <img src={ownerProfile.avatar} alt="" className="w-4 h-4 rounded-full object-cover" />
                    ) : (
                      <i className="fas fa-user text-[11px] text-purple-400"></i>
                    )}
                    <span className="truncate max-w-[120px] sm:max-w-[160px] font-bold">
                      @{ownerProfile?.username || 'colecionador'}
                    </span>
                  </div>
                )}

                {/* Folder Origin Badge */}
                {folder && (
                  <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700/80 text-xs font-semibold text-slate-300 shrink-0">
                    <i className={`fas ${folder.icon || 'fa-folder'} text-slate-400 text-xs`}></i>
                    <span className="truncate max-w-[120px] sm:max-w-[180px] text-white font-bold">{folder.name}</span>
                  </div>
                )}
                <span className="text-slate-600 hidden sm:inline">•</span>
                <div className="min-w-0">
                  <h3 className="text-sm font-black text-white uppercase tracking-tight truncate">
                    {resolvedCard.variant_code ? `${resolvedCard.variant_code} - ` : (resolvedCard.set_code ? `${resolvedCard.set_code} - ` : '')}
                    {resolvedCard.name}
                  </h3>
                </div>
              </div>

              {/* Top Controls */}
              <div className="flex items-center space-x-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsReportModalOpen(true)}
                  title="Reportar inconsistência nesta carta"
                  className="px-2.5 py-1 text-slate-400 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 rounded-lg flex items-center space-x-1.5 transition-all text-xs font-bold cursor-pointer"
                >
                  <i className="fas fa-flag text-[10px] text-red-400"></i>
                  <span className="hidden md:inline">Reportar</span>
                </button>
                <button
                  onClick={onClose}
                  className="w-8 h-8 hover:bg-slate-800 text-slate-400 hover:text-white rounded-full flex items-center justify-center transition-all shrink-0 cursor-pointer"
                  title="Fechar (Esc)"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex flex-col lg:flex-row overflow-y-auto scrollbar-hide">
              {/* Left Column: Card Image & External Market Links */}
              <div className="lg:w-[320px] p-5 sm:p-6 bg-slate-950 flex flex-col items-center justify-start space-y-4 border-b lg:border-b-0 lg:border-r border-slate-800 shrink-0">
                {/* Clickable Image for Fullscreen View */}
                <div
                  onClick={() => setFullscreenImageCard(resolvedCard)}
                  className="relative group w-full max-w-[240px] aspect-[63/88] rounded-xl overflow-hidden cursor-pointer bg-slate-950 flex items-center justify-center border border-slate-800"
                  title="Clique para expandir em tela cheia"
                >
                  <CardImage
                    src={resolvedCard.image_url}
                    alt={resolvedCard.name}
                    game={resolvedCard.game_name}
                    gameId={resolvedCard.game_id}
                    objectFit="contain"
                    className="w-full h-full rounded-xl"
                  />
                  <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center text-white space-x-2">
                    <i className="fas fa-expand text-sm"></i>
                    <span className="text-xs font-bold uppercase tracking-wider">Ampliar</span>
                  </div>
                </div>

                {/* External Actions */}
                <div className="w-full max-w-[240px] space-y-2 pt-1">
                  <a
                    href={tcgPlayerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-bold uppercase text-[10px] tracking-wider py-2.5 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                  >
                    <i className="fas fa-external-link-alt text-xs text-blue-400"></i>
                    <span>Ver no TCGPlayer</span>
                  </a>
                </div>
              </div>

              {/* Right Column: Card Identity, Hero Quantity Control, Folder Actions & Technical Specs */}
              <div className="flex-1 p-5 sm:p-7 space-y-6 bg-slate-900">
                {/* CONTROLE DE ALTERAÇÃO DE QUANTIDADE E GESTÃO DA PASTA */}
                {canEdit ? (
                  <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-3.5">
                    {/* Stepper Principal de Quantidade */}
                    <div className="space-y-3">
                      <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 flex items-center justify-between">
                        {/* Minus Button */}
                        <button
                          type="button"
                          onClick={() => onUpdateQuantity && onUpdateQuantity(card, -1)}
                          disabled={currentQuantity <= 0}
                          className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center font-bold text-sm cursor-pointer"
                          title="Diminuir 1 cópia"
                        >
                          <i className="fas fa-minus text-xs"></i>
                        </button>

                        {/* Quantity Display & Direct Input */}
                        <div className="flex flex-col items-center justify-center px-4">
                          <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">
                            Quantidade
                          </span>
                          <div className="flex items-center space-x-1">
                            <input
                              type="number"
                              min="0"
                              value={currentQuantity}
                              onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0 && onSetDirectQuantity) {
                                  onSetDirectQuantity(card, val);
                                }
                              }}
                              className="w-16 bg-transparent text-white text-center font-mono font-bold text-2xl focus:outline-none transition-colors"
                            />
                            <span className="text-xs font-bold text-slate-400">x</span>
                          </div>
                          <span className="text-[10px] text-slate-400">
                            {currentQuantity === 1 ? '1 cópia nesta pasta' : `${currentQuantity} cópias nesta pasta`}
                          </span>
                        </div>

                        {/* Plus Button */}
                        <button
                          type="button"
                          onClick={() => onUpdateQuantity && onUpdateQuantity(card, 1)}
                          className="w-10 h-10 rounded-lg bg-purple-600 hover:bg-purple-500 text-white transition-colors flex items-center justify-center font-bold text-sm cursor-pointer"
                          title="Adicionar 1 cópia"
                        >
                          <i className="fas fa-plus text-xs"></i>
                        </button>
                      </div>

                      {/* Quick Presets (Pills) */}
                      <div className="grid grid-cols-5 gap-1.5">
                        {[1, 2, 3, 4].map((qty) => (
                          <button
                            key={qty}
                            type="button"
                            onClick={() => onSetDirectQuantity && onSetDirectQuantity(card, qty)}
                            className={`py-1 px-2 rounded-lg border text-xs font-mono font-bold transition-colors cursor-pointer text-center ${
                              currentQuantity === qty
                                ? 'bg-purple-600 border-purple-500 text-white'
                                : 'bg-slate-900 hover:bg-slate-800 border-slate-800 text-slate-300 hover:text-white'
                            }`}
                          >
                            {qty}x
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => onSetDirectQuantity && onSetDirectQuantity(card, 0)}
                          disabled={currentQuantity === 0}
                          className="py-1 px-2 rounded-lg border border-slate-800 bg-slate-900 hover:bg-red-950/40 hover:border-red-800/60 text-slate-400 hover:text-red-300 text-xs font-bold transition-colors disabled:opacity-30 cursor-pointer text-center"
                          title="Zerar quantidade"
                        >
                          Zerar
                        </button>
                      </div>
                    </div>

                    {/* Actions Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                      {user && (
                        <button
                          type="button"
                          onClick={() => setIsTransferModalOpen(true)}
                          className="w-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold uppercase tracking-wider py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer"
                        >
                          <i className="fas fa-arrow-right-arrow-left text-xs text-slate-400"></i>
                          <span>Mover ou Copiar</span>
                        </button>
                      )}

                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setIsDeleteConfirmOpen(true)}
                          className="w-full bg-slate-800 hover:bg-red-950/40 text-slate-300 hover:text-red-300 border border-slate-700 hover:border-red-800/60 text-xs font-bold uppercase tracking-wider py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors cursor-pointer"
                        >
                          <i className="fas fa-trash-can text-xs text-slate-400"></i>
                          <span>Excluir desta Pasta</span>
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Bloco de Interesse & Negociação quando acessando no perfil de outro usuário */
                  <div className="bg-slate-950 border border-purple-500/30 rounded-2xl p-4 sm:p-5 space-y-4 shadow-lg shadow-purple-950/10">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2.5">
                        <div className="w-8 h-8 rounded-xl bg-purple-600/20 text-purple-400 flex items-center justify-center text-sm border border-purple-500/30">
                          <i className="fas fa-handshake"></i>
                        </div>
                        <div>
                          <span className="text-xs font-black text-white uppercase tracking-wider">
                            Interesse & Negociação
                          </span>
                          <p className="text-[11px] text-slate-400">
                            {ownerProfile?.username 
                              ? `No fichário de @${ownerProfile.username}`
                              : 'Disponível no fichário deste usuário'}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300">
                          {currentQuantity}x {currentQuantity === 1 ? 'cópia' : 'cópias'}
                        </span>
                        <span className="text-[9px] font-black px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 uppercase tracking-wider">
                          Visualização
                        </span>
                      </div>
                    </div>

                    {/* Botão de Solicitação de Contato com a carta marcada (apenas se estiver na Offerlist) */}
                    {isOfferlist && (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={handleContactOwner}
                          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-black text-xs sm:text-sm uppercase tracking-wider py-3 px-4 rounded-xl flex items-center justify-center space-x-2 shadow-lg shadow-purple-600/25 transition-all active:scale-[0.98] cursor-pointer"
                        >
                          <i className="fas fa-paper-plane text-sm"></i>
                          <span>Tenho Interesse • Solicitar Contato</span>
                        </button>
                        <p className="text-[10px] text-slate-400 text-center">
                          Abre o CardLink com <strong className="text-purple-300">{resolvedCard.name}</strong> marcada na sua solicitação de contato.
                        </p>
                      </div>
                    )}

                    {/* Ações Rápidas: Salvar na Wishlist ou Copiar para Pastas Pessoais */}
                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/80">
                      <button
                        type="button"
                        onClick={handleQuickAddToWishlist}
                        disabled={isAddingToWishlist}
                        className="bg-slate-900 hover:bg-slate-800 text-pink-400 hover:text-pink-300 border border-slate-800 hover:border-pink-500/30 text-xs font-bold py-2.5 px-3 rounded-xl flex items-center justify-center space-x-1.5 transition-colors cursor-pointer disabled:opacity-50"
                        title="Adicionar à sua Wishlist pessoal"
                      >
                        <i className={`fas fa-heart text-xs ${isAddingToWishlist ? 'animate-pulse' : ''}`}></i>
                        <span>{isInWishlist ? 'Na Wishlist ✓' : 'Salvar na Wishlist'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setTransferAction('copy');
                          setIsTransferModalOpen(true);
                        }}
                        className="bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 text-xs font-bold py-2.5 px-3 rounded-xl flex items-center justify-center space-x-1.5 transition-colors cursor-pointer"
                        title="Copiar para uma das suas pastas pessoais"
                      >
                        <i className="fas fa-copy text-xs text-purple-400"></i>
                        <span>Copiar para Pastas</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Atributos & Informações da Tabela cards */}
                {fields.length > 0 && (
                  <div className="space-y-2.5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                      {fields.map((f, i) => (
                        <DetailField
                          key={i}
                          label={f.label}
                          value={f.value}
                          highlight={f.highlight}
                          isCircle={f.isCircle}
                          icon={f.icon}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer Nav: Browse Cards in this Folder */}
            <div className="px-5 sm:px-6 py-3 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
              {cardsList.length > 0 && currentCardIndex !== -1 && onSelectCard ? (
                <div className="flex items-center justify-between w-full">
                  <button
                    disabled={currentCardIndex <= 0}
                    onClick={() => onSelectCard(getTargetCard(cardsList[currentCardIndex - 1]))}
                    className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-25 disabled:cursor-not-allowed text-xs font-bold text-white transition-colors flex items-center space-x-1.5 cursor-pointer"
                    title="Carta anterior (Seta esquerda)"
                  >
                    <i className="fas fa-chevron-left text-[10px]"></i>
                    <span className="hidden sm:inline">Anterior</span>
                  </button>

                  <div className="bg-slate-800 px-3.5 py-1 rounded-full border border-slate-700 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    {currentCardIndex + 1} de {cardsList.length} na pasta
                  </div>

                  <button
                    disabled={currentCardIndex >= cardsList.length - 1}
                    onClick={() => onSelectCard(getTargetCard(cardsList[currentCardIndex + 1]))}
                    className="px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 disabled:opacity-25 disabled:cursor-not-allowed text-xs font-bold text-white transition-colors flex items-center space-x-1.5 cursor-pointer"
                    title="Próxima carta (Seta direita)"
                  >
                    <span className="hidden sm:inline">Próxima</span>
                    <i className="fas fa-chevron-right text-[10px]"></i>
                  </button>
                </div>
              ) : (
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Detalhes da Carta na Pasta
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Submodal: Transfer / Copy Card to Another Folder */}
      <AnimatePresence>
        {isTransferModalOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/80">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-xl p-5 space-y-4 relative">
              <button
                type="button"
                onClick={() => setIsTransferModalOpen(false)}
                disabled={isSubmittingTransfer}
                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="fas fa-times text-sm"></i>
              </button>

              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 flex items-center justify-center text-sm shrink-0">
                  <i className={`fas ${canEdit ? 'fa-arrow-right-arrow-left' : 'fa-copy'}`}></i>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {canEdit ? 'Mover ou Copiar Carta' : 'Salvar Cópia nas Minhas Pastas'}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {canEdit ? 'Transfira para outra pasta ou fichário' : `Adicione uma cópia de "${resolvedCard.name}" em suas listas pessoais`}
                  </p>
                </div>
              </div>

              {/* Action selection: Move vs Copy */}
              {canEdit && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                    Tipo de Ação
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTransferAction('copy')}
                      className={`py-2 px-3 rounded-lg border text-xs font-bold transition-colors flex items-center justify-center space-x-2 cursor-pointer ${
                        transferAction === 'copy'
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      <i className="fas fa-copy text-xs"></i>
                      <span>Copiar (Duplicar)</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTransferAction('move')}
                      className={`py-2 px-3 rounded-lg border text-xs font-bold transition-colors flex items-center justify-center space-x-2 cursor-pointer ${
                        transferAction === 'move'
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-slate-950 border-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      <i className="fas fa-arrow-right text-xs"></i>
                      <span>Mover (Transferir)</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Target Folder Selection */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase text-slate-400 tracking-wider">
                  Pasta de Destino
                </label>
                <select
                  value={targetDestination}
                  onChange={(e) => setTargetDestination(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 text-white text-xs font-semibold rounded-lg px-3 py-2 focus:outline-none focus:border-purple-500 cursor-pointer"
                >
                  <optgroup label="Pastas do Sistema">
                    {folder?.id !== 'colecao' && <option value="colecao">Minha Coleção</option>}
                    {folder?.id !== 'wishlist' && <option value="wishlist">Wishlist</option>}
                    {folder?.id !== 'offerlist' && <option value="offerlist">Offerlist (Trocas)</option>}
                  </optgroup>
                  {userBinders.filter(b => String(b.id) !== String(folder?.id)).length > 0 && (
                    <optgroup label="Seus Fichários Personalizados">
                      {userBinders
                        .filter(b => String(b.id) !== String(folder?.id))
                        .map(b => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                </select>
              </div>

              {/* Submit Buttons */}
              <div className="pt-2 flex items-center space-x-2.5">
                <button
                  type="button"
                  onClick={() => setIsTransferModalOpen(false)}
                  disabled={isSubmittingTransfer}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold py-2 rounded-lg text-xs transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleExecuteTransfer}
                  disabled={isSubmittingTransfer}
                  className="flex-1 bg-purple-600 hover:bg-purple-500 text-white font-bold uppercase tracking-wider py-2 rounded-lg text-xs transition-colors flex items-center justify-center space-x-1.5 cursor-pointer disabled:opacity-40"
                >
                  {isSubmittingTransfer ? (
                    <>
                      <i className="fas fa-spinner fa-spin text-xs"></i>
                      <span>Processando...</span>
                    </>
                  ) : (
                    <>
                      <i className={`fas ${transferAction === 'move' ? 'fa-arrow-right' : 'fa-copy'} text-xs`}></i>
                      <span>{transferAction === 'move' ? 'Confirmar Mudança' : 'Adicionar Cópia'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Submodal: Confirmação Própria de Exclusão da Carta */}
      <AnimatePresence>
        {isDeleteConfirmOpen && (
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-3.5 sm:p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl sm:rounded-3xl p-5 sm:p-6 space-y-5 shadow-2xl relative"
            >
              <button
                type="button"
                onClick={() => !isDeletingCard && setIsDeleteConfirmOpen(false)}
                disabled={isDeletingCard}
                className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors disabled:opacity-40 cursor-pointer"
              >
                <i className="fas fa-times text-base"></i>
              </button>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-center text-lg shrink-0">
                  <i className="fas fa-trash-can"></i>
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Remover Carta</h3>
                  <p className="text-xs text-slate-400">Confirmação de exclusão</p>
                </div>
              </div>

              {/* Pré-visualização da carta */}
              <div className="flex items-center space-x-3 sm:space-x-4 p-3 rounded-xl bg-slate-950/80 border border-slate-800/80">
                <div className="w-12 sm:w-14 aspect-[2.5/3.5] rounded-lg overflow-hidden bg-slate-900 shrink-0 border border-slate-800">
                  <CardImage
                    src={resolvedCard.image_url}
                    alt={resolvedCard.name}
                    game={resolvedCard.game_name}
                    gameId={resolvedCard.game_id}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-0.5 min-w-0 flex-1">
                  <h4 className="text-xs sm:text-sm font-bold text-white truncate">{resolvedCard.name}</h4>
                  <p className="text-[11px] text-slate-400">
                    Pasta: <span className="text-purple-400 font-semibold">{folder?.name}</span>
                  </p>
                  <p className="text-[10px] text-slate-500 font-mono">
                    Quantidade atual: <span className="text-white font-bold">{currentQuantity}</span>
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-400 leading-relaxed">
                Tem certeza que deseja remover esta carta da pasta? Ela será retirada desta listagem.
              </p>

              <div className="flex items-center space-x-3 pt-1">
                <button
                  type="button"
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  disabled={isDeletingCard}
                  className="w-1/2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 font-bold py-2.5 sm:py-3 rounded-xl transition-all text-xs uppercase tracking-widest cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  disabled={isDeletingCard}
                  className="w-1/2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-black py-2.5 sm:py-3 rounded-xl shadow-lg shadow-red-600/30 transition-all text-xs uppercase tracking-widest flex items-center justify-center space-x-2 cursor-pointer"
                >
                  {isDeletingCard ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      <span>Removendo...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash-can text-xs"></i>
                      <span>Sim, Excluir</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Fullscreen Card Image Modal */}
      <AnimatePresence>
        {fullscreenImageCard && (
          <div
            onClick={() => setFullscreenImageCard(null)}
            className="fixed inset-0 z-[130] flex flex-col items-center justify-center p-4 bg-black/90 cursor-zoom-out"
          >
            <div className="relative max-w-lg w-full aspect-[63/88] rounded-xl overflow-hidden bg-slate-900 border border-slate-800 flex items-center justify-center">
              <CardImage
                src={fullscreenImageCard.image_url}
                alt={fullscreenImageCard.name}
                game={fullscreenImageCard.game_name}
                gameId={fullscreenImageCard.game_id}
                objectFit="contain"
                className="w-full h-full"
              />
            </div>
            <p className="mt-4 text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center space-x-2">
              <i className="fas fa-times text-xs"></i>
              <span>Clique em qualquer lugar para fechar</span>
            </p>
          </div>
        )}
      </AnimatePresence>

      {/* Card Bug Report Modal */}
      <CardBugReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        card={resolvedCard}
        cardCode={resolvedCard.variant_code || resolvedCard.set_code || resolvedCard.external_id}
        gameName={resolvedCard.game_name}
        cardSet={resolvedCard.set_code || ''}
        cardRarity={resolvedCard.rarity || ''}
        activeImage={resolvedCard.image_url || ''}
        selectedVariantInfo={null}
      />
    </>
  );
};
